import { createRoute, redirect, type AnyRoute } from "@tanstack/react-router";
import { Scale } from "lucide-react";

import type { FeatureModule } from "@/features/types";
import { guarded } from "@/lib/auth";

import {
  defaultClosePeriod,
  type ParentChildStageKey,
  type ReviewStatus,
} from "./api";
import type { ChangeKind } from "./billedActuals";
import { ReconciliationOverviewPage } from "./pages/ReconciliationOverviewPage";
import { ReconciliationStepPage } from "./pages/ReconciliationStepPage";

/** Search params on /reconciliation/$period/$stepKey — worklist state (filters
 *  + which row's drawer is open) kept in the URL so a filtered view or one
 *  record's review is shareable. Steps read only the params they use. */
export interface ReconStepSearch {
  q?: string;
  /** Comma list of review statuses, e.g. "pending,flagged". */
  review?: string;
  /** Comma list of change kinds, e.g. "new,stopped". */
  change?: string;
  mismatch?: boolean;
  mrr?: boolean;
  /** R-01: QB id of the account open in the review drawer. */
  account?: number;
  /** R-14: Cinch contract id open in the review drawer. */
  contract?: string;
  /** R-16: item_key of the relationship change open in the review drawer.
   *  Distinct from `change`, which is R-01's billed-change-kind filter. */
  link?: string;
  /** R-16: comma list of month stages, e.g. "approved,verified". */
  stage?: string;
  expanded?: boolean;
}

/** Search params on /reconciliation/$period — the overview's tab switch. */
export interface ReconOverviewSearch {
  tab?: "admin";
}

function validateReconOverviewSearch(raw: Record<string, unknown>): ReconOverviewSearch {
  return raw.tab === "admin" ? { tab: "admin" } : {};
}

const REVIEW_VALUES: ReviewStatus[] = ["pending", "approved", "flagged"];
const CHANGE_VALUES: ChangeKind[] = ["new", "stopped", "increased", "decreased", "flat"];
const STAGE_VALUES: ParentChildStageKey[] = [
  "requested",
  "approved",
  "verified",
  "rejected",
  "cancelled",
];

function commaList(raw: unknown, allowed: string[]): string | undefined {
  if (typeof raw !== "string" || !raw) return undefined;
  const vals = raw.split(",").filter((v) => allowed.includes(v));
  return vals.length ? vals.join(",") : undefined;
}

function validateReconStepSearch(raw: Record<string, unknown>): ReconStepSearch {
  const out: ReconStepSearch = {};
  if (typeof raw.q === "string" && raw.q) out.q = raw.q;
  out.review = commaList(raw.review, REVIEW_VALUES);
  out.change = commaList(raw.change, CHANGE_VALUES);
  if (raw.mismatch === true || raw.mismatch === "1" || raw.mismatch === "true") out.mismatch = true;
  if (raw.mrr === true || raw.mrr === "1" || raw.mrr === "true") out.mrr = true;
  if (raw.account !== undefined && raw.account !== "") {
    const n = Number(raw.account);
    if (Number.isFinite(n)) out.account = n;
  }
  if (typeof raw.contract === "string" && raw.contract) out.contract = raw.contract;
  if (typeof raw.link === "string" && raw.link) out.link = raw.link;
  out.stage = commaList(raw.stage, STAGE_VALUES);
  if (raw.expanded === true || raw.expanded === "1" || raw.expanded === "true") {
    out.expanded = true;
  }
  return out;
}

export const reconciliationModule: FeatureModule = {
  key: "reconciliation",
  nav: {
    label: "Reconciliation",
    icon: Scale,
    to: "/reconciliation",
    requires: "reconciliation.read",
    order: 30,
    group: "Operations",
  },
  routes: (rootRoute) => [
    // Bare /reconciliation → the current month's close.
    createRoute({
      getParentRoute: () => rootRoute,
      path: "/reconciliation",
      beforeLoad: () => {
        throw redirect({
          to: "/reconciliation/$period",
          params: { period: defaultClosePeriod() },
        });
      },
      component: () => null,
    }),
    createRoute({
      getParentRoute: () => rootRoute,
      path: "/reconciliation/$period",
      component: guarded("reconciliation.read", ReconciliationOverviewPage),
      validateSearch: validateReconOverviewSearch,
    }),
    createRoute({
      getParentRoute: () => rootRoute,
      path: "/reconciliation/$period/$stepKey",
      component: guarded("reconciliation.read", ReconciliationStepPage),
      validateSearch: validateReconStepSearch,
    }),
  ] as unknown as AnyRoute[],
};
